'use client';

import { Modal } from '@shared/ui';
import type { BaseModalProps } from '@shared/types/modal.types';

interface TaskCreatedModalProps extends BaseModalProps {
  taskId?: number;
  taskTitle?: string;
  onViewTask?: () => void;
}

/**
 * Task Created Modal - confirmation modal after task creation
 * Usage: const { open } = useModal('task-created'); open({ taskId: 123, taskTitle: 'Fix my sink' });
 */
export function TaskCreatedModal({
  isOpen,
  onClose,
  taskId,
  taskTitle,
  onViewTask,
}: TaskCreatedModalProps) {
  const handleViewTask = () => {
    onViewTask?.();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      animation="zoom"
      position="center"
      closeOnOverlayClick={false}
      showCloseButton={false}
    >
      <div className="text-center py-6">
        {/* Success Icon */}
        <div className="mx-auto w-16 h-16 mb-6 rounded-full bg-green-100 flex items-center justify-center">
          <svg
            className="w-8 h-8 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>

        {/* Title */}
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Task Created Successfully!
        </h3>

        {/* Task Info */}
        {taskTitle && (
          <p className="text-gray-600 mb-4">
            <span className="font-medium">&quot;{taskTitle}&quot;</span> has been posted
          </p>
        )}

        {/* Description */}
        <p className="text-gray-500 mb-8">
          Your task is now live. You&apos;ll receive notifications when contractors send
          proposals.
        </p>

        {/* Task ID Badge */}
        {taskId && (
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-sm mb-6">
            Task ID: #{taskId}
          </div>
        )}

        {/* Actions */}
        <div className="space-y-3">
          <button
            onClick={handleViewTask}
            className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            View Task
          </button>
          <button
            onClick={onClose}
            className="w-full px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
}
